/**
 * Who a phone caller is, and which stay their label names. Pure, because the
 * lock screen (the API's caller label) and the in-call view (the host app's
 * lookup) must name the same guest and the same room.
 */

import { isDeadStay, type ReservationStatus } from "./enums";

const timeOf = (value: Date | string | null): number =>
  value === null ? Number.NEGATIVE_INFINITY : new Date(value).getTime();

/**
 * The guest a caller's number names. A direct phone match wins over an
 * identity's; among several records sharing it, the oldest (the original
 * profile rather than a later duplicate), then the lowest id, so the same
 * guest wins on every call.
 */
export function pickCallerGuest<
  T extends { createdAt: Date | string; id: string },
>(byPhone: readonly T[], byIdentity: readonly T[]): T | null {
  const candidates = byPhone.length > 0 ? byPhone : byIdentity;
  return candidates.reduce<T | null>((best, guest) => {
    if (best === null) {
      return guest;
    }
    const age = timeOf(guest.createdAt) - timeOf(best.createdAt);
    return age < 0 || (age === 0 && guest.id < best.id) ? guest : best;
  }, null);
}

interface CallerStay {
  checkInAt: Date | string | null;
  id: string;
  status: ReservationStatus;
  unitCode: string | null | undefined;
}

function byCodePoint(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  return a < b ? -1 : 1;
}

/** A named room before an unassigned one, then by code, "9" before "10". */
function compareRooms(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  if (!(a && b)) {
    return (a ? 0 : 1) - (b ? 0 : 1);
  }
  // "01" and "1" tie numerically; the code point order still tells them apart.
  return a.localeCompare(b, "en", { numeric: true }) || byCodePoint(a, b);
}

/**
 * The stay a caller's label names, across the stays they are registered on and
 * the ones they booked (a booker calls before anyone has registered), dead
 * stays left out: a checked-in stay, else the newest check-in. A
 * stay with no check-in sorts oldest. One booking's rooms are rows of the same
 * stay: the room the caller is registered in wins, then the first by code,
 * whatever order each side read them in.
 */
export function pickCallerStay<T extends CallerStay>(
  registered: readonly T[],
  booked: readonly T[]
): T | null {
  const isInHouse = (stay: T) => stay.status === "checked_in";
  const ranked = [
    ...registered.map((stay) => ({ booked: 0, stay })),
    ...booked.map((stay) => ({ booked: 1, stay })),
  ].filter(({ stay }) => !isDeadStay(stay.status));
  ranked.sort(
    (a, b) =>
      Number(isInHouse(b.stay)) - Number(isInHouse(a.stay)) ||
      timeOf(b.stay.checkInAt) - timeOf(a.stay.checkInAt) ||
      byCodePoint(b.stay.id, a.stay.id) ||
      a.booked - b.booked ||
      compareRooms(a.stay.unitCode, b.stay.unitCode)
  );
  return ranked[0]?.stay ?? null;
}
